import {AnalyticsTab, DiagramMode, Store} from './store';
import moment from 'moment';
import _ from 'lodash';
import {TimeSeriesEntry} from '../apis/TimeSeriesEntry';
import {DateRange, TimeState} from './TimeIntervalStore';
import {KpiDataPackage, KpiPackage, VisibleAttribute} from '../types/Chart';
import {LineChartData} from '../views/charts/LineChart';
import {BarChartData} from '../views/charts/BarChart';
import {HistoryHandler, HistoryItem} from './MemoryBarStore';
import {isKPI, KPI} from '../kpi';
import {observable} from 'mobx';
import {color, ColorHelper} from 'csx';
import {Channel} from '../types/Channel';
import {ChannelState} from './ChannelStore';
import DataVersionChecker, {DataVersionInfo} from '../utility/DataVersionChecker';
import {DateUtils} from '../utility/DateUtils';
import {DIAGRAM_COLORS} from '../utility/ColorUtils';
import {BlackColor} from '../styles/colors';

export const hasMinimumKpiInformation = (value: any): value is MinimumKpiInformation => {
    return value && typeof value.channelName === 'string' && typeof value.searchtermOrProductId === 'string' && isKPI(value.kpi);
};

export const isChannelDiagramRequest = (value: any): value is ChannelDiagramRequest => {
    return value && typeof value.channelName === 'string' && isKPI(value.kpi);
};

export interface MinimumKpiInformation {
    channelName: string;
    searchtermOrProductId: string;
    kpi: KPI;
    testId?: string;
    variantId?: string;
}

export interface TermColorIndex extends MinimumKpiInformation {
    colorIndex?: number;
}

export interface LoadedDiagramData extends KpiPackage {}
export interface SelectedGraphInDiagram extends MinimumKpiInformation {}

export interface ChannelDiagramRequest {
    channelName: string;
    kpi: KPI;
    title?: string;
    testId?: string;
    variantId?: string;
}

export interface LineDiagramRequest extends MinimumKpiInformation {
    title?: string;
}

export const kpiPackagesMatch = (dataEntry: KpiPackage, kpiRequestInfo: KpiPackage) => {
    const {channel, isChannelDiagram, isCompareDiagram, kpi, searchtermOrProductId, testId, variantId} = kpiRequestInfo;
    return (
        dataEntry.kpi === kpi &&
        dataEntry.channel.name === channel.name &&
        dataEntry.searchtermOrProductId === searchtermOrProductId &&
        dataEntry.isCompareDiagram === isCompareDiagram &&
        dataEntry.isChannelDiagram === isChannelDiagram &&
        dataEntry.testId === testId &&
        dataEntry.variantId === variantId
    );
};

abstract class GeneralLineDiagramStore implements HistoryHandler {
    public dataVersionChecker: DataVersionChecker;
    @observable public updateDiagram = 0;
    public diagramData: LineChartData[] = [];
    @observable public termColorIndexes: TermColorIndex[] = [];
    @observable public selectedGraphInDiagram: SelectedGraphInDiagram[] = [];
    @observable public requestedNormalDiagrams: LineDiagramRequest[] = [];
    @observable public requestedChannelDiagrams: ChannelDiagramRequest[] = [];
    @observable public customDatepickerFoldout = false;
    @observable public loadedDiagramData: LoadedDiagramData[] = [];
    @observable public diagramYScaleAbsolute = true;
    public dependantAnalyticsTab?: AnalyticsTab;
    @observable public coloredDropBoxesAreVisible = false;

    // SEARCH
    constructor(protected readonly mainStore: Store, protected readonly channelState: ChannelState, protected readonly timeState: TimeState) {
        this.dataVersionChecker = new DataVersionChecker();
    }

    public getPartialHistory = (): Partial<HistoryItem> => ({
        activeDiagramItems: {
            diagrams: this.requestedNormalDiagrams.map((diagramData) => ({
                ...diagramData,
                channel: diagramData.channelName,
            })),
            channelDiagrams: this.requestedChannelDiagrams.map((channelDiagramData) => ({
                ...channelDiagramData,
                channel: channelDiagramData.channelName,
            })),
            showAbsoluteValues: this.diagramYScaleAbsolute,
        },
    });

    public getDateRangeConversion = (): ((dateRange: DateRange) => DateRange) => {
        switch (this.mainStore.diagramMode) {
            case DiagramMode.TwentyFourHourMode:
            case DiagramMode.Hourly:
                return DateUtils.toHourlyDateRange;
            case DiagramMode.Daily:
                return DateUtils.toDailyDateRange;
            default:
                throw Error('DiagramMode not supported for dateRange conversion: ' + this.mainStore.diagramMode);
        }
    };

    public applyHistory(history: HistoryItem): void {
        if (this.dependantAnalyticsTab !== history.analyticsTab) {
            return;
        }

        const {activeDiagramItems} = history;

        this.termColorIndexes = [];
        this.diagramData = [];
        this.loadedDiagramData = [];

        this.requestedChannelDiagrams = activeDiagramItems.channelDiagrams.map((channelDiagram) => {
            this.updateTermColorIndexes(
                channelDiagram.channel,
                channelDiagram.kpi,
                channelDiagram.channel,
                undefined,
                channelDiagram.testId,
                channelDiagram.variantId
            );
            return {
                ...channelDiagram,
                channelName: channelDiagram.channel,
            };
        });
        this.requestedNormalDiagrams = activeDiagramItems.diagrams.map((diagram) => {
            this.updateTermColorIndexes(diagram.searchtermOrProductId, diagram.kpi, diagram.channel, undefined, diagram.testId, diagram.variantId);
            return {
                ...diagram,
                channelName: diagram.channel,
            };
        });
        this.diagramYScaleAbsolute = activeDiagramItems.showAbsoluteValues;
    }

    public updateDiagramView() {
        /**
         * Force update view because its observable
         * @type {boolean}
         */
        this.updateDiagram++;
    }

    public updateTermColorIndexes(
        searchtermOrProductId: string,
        kpi: KPI,
        channelName: string,
        colorIndex?: number,
        testId?: string,
        variantId?: string
    ): void {
        /**
         * Almost every diagram should get its own color. Only exception are are diagrams resulting from
         * time range comparisons, they will have the same color, only the line pattern will differ
         * line color, even on other channels
         * @type {{}}
         */

        this.termColorIndexes.push({
            channelName,
            searchtermOrProductId,
            kpi,
            colorIndex: colorIndex || this.getFirstUnusedColorIndex(),
            testId,
            variantId,
        });
    }

    public getFirstUnusedColorIndex(): number {
        return DIAGRAM_COLORS.findIndex((rbgaColor, index) => !this.colorIndexAlreadyUsed(index));
    }

    public colorIndexAlreadyUsed(index: number) {
        return this.termColorIndexes.some((termColorEntry) => termColorEntry.colorIndex === index);
    }

    public removeFromSelectedGraphInDiagram(channel: string, searchtermOrProductId: string, kpi: KPI, testId?: string, variantId?: string) {
        const index = this.selectedGraphInDiagram.findIndex(
            (selectedGraph) =>
                selectedGraph.channelName === channel &&
                selectedGraph.kpi === kpi &&
                selectedGraph.searchtermOrProductId === searchtermOrProductId &&
                selectedGraph.testId === testId &&
                selectedGraph.variantId === variantId
        );

        if (index !== -1) {
            this.selectedGraphInDiagram.splice(index, 1);
        }
    }

    public selectGraph = (searchtermOrProductId: string, channelName: string, kpi: KPI, testId?: string, variantId?: string) => {
        /**
         * Add and toggle SelectedSearchterm Item in the state. The corresponding graph will be marked with a higher
         * opacity than the other graphes
         */

        if (this.checkIfGraphIsSelected(searchtermOrProductId, channelName, kpi, false, testId, variantId)) {
            const selectedIndex = this.selectedGraphInDiagram.findIndex(
                (selectedGraph) =>
                    selectedGraph.channelName === channelName &&
                    selectedGraph.kpi === kpi &&
                    selectedGraph.searchtermOrProductId === searchtermOrProductId &&
                    selectedGraph.testId === testId &&
                    selectedGraph.variantId === variantId
            );

            if (selectedIndex !== -1) {
                this.selectedGraphInDiagram.splice(selectedIndex, 1);
            }
        } else {
            this.selectedGraphInDiagram.push({
                channelName,
                kpi,
                searchtermOrProductId,
                testId,
                variantId,
            });
        }

        if (this.allDiagramsAreSelected()) {
            this.selectedGraphInDiagram = [];
        }

        this.updateDiagramView();
    };

    public allDiagramsAreSelected() {
        let allDiagramsSelected = false;
        const visibleDiagramsCount = this.requestedNormalDiagrams.length + this.requestedChannelDiagrams.length;
        if (visibleDiagramsCount === this.selectedGraphInDiagram.length) {
            allDiagramsSelected = true;
        }
        return allDiagramsSelected;
    }

    public checkIfGraphIsSelected = (
        searchtermOrProductId: string,
        channelName: string,
        kpi: string,
        noSelectedEqualsTrue = false,
        testId?: string,
        variantId?: string
    ): boolean => {
        if (noSelectedEqualsTrue && this.selectedGraphInDiagram.length === 0) {
            return true;
        }

        return this.selectedGraphInDiagram.some(
            (selectedGraph) =>
                selectedGraph.channelName === channelName &&
                selectedGraph.kpi === kpi &&
                selectedGraph.searchtermOrProductId === searchtermOrProductId &&
                selectedGraph.testId === testId &&
                selectedGraph.variantId === variantId
        );
    };

    public checkIfGraphIsLoaded = (searchtermOrProductId: string, channelName: string, kpi: string, testId?: string, variantId?: string): boolean => {
        return this.loadedDiagramData.some(
            (loadedDiagram) =>
                loadedDiagram.channel.name === channelName &&
                loadedDiagram.kpi === kpi &&
                loadedDiagram.searchtermOrProductId === searchtermOrProductId &&
                loadedDiagram.testId === testId &&
                loadedDiagram.variantId === variantId
        );
    };

    // FIXME: Partly unused. Only used with (true)
    public toggleCustomDatepickerFoldout(close: boolean) {
        if (close) {
            this.customDatepickerFoldout = false;
        } else {
            this.customDatepickerFoldout = !this.customDatepickerFoldout;
        }
    }

    public getXScaleYCoordinate() {
        const windowHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
        return windowHeight * 0.4 - 128;
    }

    public turnDateStringToTimestamp(dateString: string) {
        /**
         * returns the equivalent of the date string as a timestamp in seconds
         */
        const dateMatch = dateString.match(/(\d\d)\.(\d\d)\.(\d\d\d\d) (\d\d):(\d\d)/);
        if (dateMatch && dateMatch.length === 6) {
            const dateFromMatch = new Date(`${dateMatch[3]}-${dateMatch[2]}-${dateMatch[1]}T${dateMatch[4]}:${dateMatch[5]}:00`);
            return dateFromMatch.getTime() / 1000;
        }
        return 0;
    }

    public changeKpiVariable = (changedKpi: KPI | 'mixed') => {
        if (changedKpi !== 'mixed') {
            this.changeTermColorIndexesToFitNewAttribute(changedKpi);

            this.requestedNormalDiagrams.forEach((data) => {
                data.kpi = changedKpi;
            });

            this.requestedChannelDiagrams.forEach((data) => {
                data.kpi = changedKpi;
            });

            this.requestedNormalDiagrams = _.uniqBy(this.requestedNormalDiagrams, (item) => {
                const {channelName, kpi, searchtermOrProductId, testId, variantId} = item;
                return channelName + kpi + searchtermOrProductId + testId + variantId;
            });

            this.requestedChannelDiagrams = _.uniqBy(this.requestedChannelDiagrams, (item) => {
                const {channelName, kpi, variantId, testId} = item;
                return channelName + kpi + variantId + testId;
            });

            this.freeStuckDiagramColors();

            this.reloadDataFromBackend();
        }
    };

    private freeStuckDiagramColors() {
        const channelSearchtermCheck = (searchtermOrProductId: string, diagramRequest: LineDiagramRequest | ChannelDiagramRequest): boolean => {
            if (hasMinimumKpiInformation(diagramRequest)) {
                return searchtermOrProductId === diagramRequest.searchtermOrProductId;
            } else if (isChannelDiagramRequest(diagramRequest)) {
                return searchtermOrProductId === diagramRequest.channelName;
            }
            throw Error('TermColor check cannot be applied to types other than LineDiagramRequest and ChannelDiagramRequest');
        };
        this.termColorIndexes = [...this.requestedNormalDiagrams, ...this.requestedChannelDiagrams].reduce(
            (usedTermColorIndexes: TermColorIndex[], requestedDiagram) => {
                const matchingTermColorItem = this.termColorIndexes.find(
                    (termColorItem) =>
                        termColorItem.kpi === requestedDiagram.kpi &&
                        channelSearchtermCheck(termColorItem.searchtermOrProductId, requestedDiagram) &&
                        termColorItem.channelName === requestedDiagram.channelName &&
                        termColorItem.variantId === requestedDiagram.variantId &&
                        termColorItem.testId === requestedDiagram.testId
                );

                if (matchingTermColorItem) {
                    usedTermColorIndexes.push(matchingTermColorItem);
                }
                return usedTermColorIndexes;
            },
            []
        );
    }

    /**
     * Applies the attribute to all termColorIndexes
     * @param kpi
     */
    public changeTermColorIndexesToFitNewAttribute(kpi: KPI) {
        this.termColorIndexes = this.termColorIndexes.map((termColorItem) => {
            return {
                ...termColorItem,
                kpi,
            };
        });
    }

    public abstract addTermToDiagram(
        searchtermOrProductId: string,
        kpi: KPI,
        title: string,
        channelName: string,
        colorIndex?: number,
        testId?: string,
        variantId?: string
    ): void;
    public abstract addChannelToDiagram(
        channelName: string,
        kpi: KPI,
        title?: string,
        colorIndex?: number,
        testId?: string,
        variantId?: string
    ): void;
    protected abstract reloadDiagramData(): void;

    public getKpiPackagesOfComparisonDiagrams(): KpiPackage[] {
        if (this.diagramData.length > 0) {
            const firstEntryWithCompareData = this.diagramData.find((singleDiagramData) =>
                singleDiagramData.data.some((kpiData) => kpiData.isCompareDiagram)
            );
            if (firstEntryWithCompareData) {
                return firstEntryWithCompareData.data.filter((kpiData) => kpiData.isCompareDiagram);
            }
        }
        return [];
    }

    public removeCompareRangeDiagrams() {
        this.getKpiPackagesOfComparisonDiagrams().forEach((kpiPackage) => {
            this.removeTermFromDiagramData(kpiPackage.channel.name, kpiPackage.searchtermOrProductId, kpiPackage.kpi, true);
        });
        this.updateDiagramView();
    }

    public removeFromVisibleChannelDiagramData(
        searchtermOrProductId: string,
        channelName: string,
        kpi: string,
        isChannelDiagram: boolean,
        testId?: string,
        variantId?: string
    ) {
        const {requestedChannelDiagrams, requestedNormalDiagrams} = this;

        if (isChannelDiagram) {
            const indexChannel = requestedChannelDiagrams.findIndex(
                (channelItem) =>
                    channelItem.channelName === channelName &&
                    channelItem.kpi === kpi &&
                    channelItem.variantId === variantId &&
                    channelItem.testId === testId
            );

            if (indexChannel !== -1) {
                this.requestedChannelDiagrams.splice(indexChannel, 1);
            }
        } else {
            const indexNormal = requestedNormalDiagrams.findIndex(
                (normalItem) =>
                    normalItem.searchtermOrProductId === searchtermOrProductId &&
                    normalItem.kpi === kpi &&
                    normalItem.channelName === channelName &&
                    normalItem.variantId === variantId &&
                    normalItem.testId === testId
            );
            if (indexNormal !== -1) {
                this.requestedNormalDiagrams.splice(indexNormal, 1);
            }
        }
    }

    private removeFromTermColorIndexes(channelName: string, searchtermOrProductId: string, kpi: string, testId?: string, variantId?: string) {
        const matchingIndex = this.termColorIndexes.findIndex(
            (termColorIndex) =>
                termColorIndex.channelName === channelName &&
                termColorIndex.kpi === kpi &&
                termColorIndex.searchtermOrProductId === searchtermOrProductId &&
                termColorIndex.testId === testId &&
                termColorIndex.variantId === variantId
        );

        if (matchingIndex !== -1) {
            this.termColorIndexes.splice(matchingIndex, 1);
        }
    }

    public removeTermFromDiagram = (
        searchtermOrProductId: string,
        channelName: string,
        kpi: KPI,
        isChannelDiagram = false,
        testId?: string,
        variantId?: string
    ) => {
        /**
         * Removes a selected graph from the diagram
         */
        this.removeFromVisibleChannelDiagramData(searchtermOrProductId, channelName, kpi, isChannelDiagram, testId, variantId);
        this.removeTermFromDiagramData(channelName, searchtermOrProductId, kpi, false, testId, variantId);
        this.removeFromTermColorIndexes(channelName, searchtermOrProductId, kpi, testId, variantId);
        this.removeFromSelectedGraphInDiagram(channelName, searchtermOrProductId, kpi, testId, variantId);
        this.updateDiagramView();
    };

    public removeTermFromDiagramData(
        channelName: string,
        searchtermOrProductId: string,
        kpi: string,
        allowOnlyCompareMatch = false,
        testId?: string,
        variantId?: string
    ) {
        /**
         * Clears the diagram data from a specific term an channel combination
         * @type {Number}
         */
        const matchAll = (data: KpiPackage): boolean =>
            data.channel.name === channelName &&
            data.searchtermOrProductId === searchtermOrProductId &&
            data.kpi === kpi &&
            data.testId === testId &&
            data.variantId === variantId;
        const matchOnlyCompare = (data: KpiPackage): boolean =>
            data.channel.name === channelName &&
            data.searchtermOrProductId === searchtermOrProductId &&
            data.kpi === kpi &&
            data.isCompareDiagram &&
            data.testId === testId &&
            data.variantId === variantId;

        const compareToUse = allowOnlyCompareMatch ? matchOnlyCompare : matchAll;

        this.diagramData.forEach((pointInTimeEntry) => {
            const {data} = pointInTimeEntry;
            let dataLength = data.length;
            while (dataLength--) {
                if (compareToUse(data[dataLength])) {
                    data.splice(dataLength, 1);
                }
            }
        });

        let diagramDataLength = this.diagramData.length;
        while (diagramDataLength--) {
            if (this.diagramData[diagramDataLength].data.length === 0) {
                this.diagramData.splice(diagramDataLength, 1);
            }
        }

        this.removeFromLoadedDiagramDataList(channelName, searchtermOrProductId, kpi, allowOnlyCompareMatch);
    }

    public removeFromLoadedDiagramDataList(channelName: string, searchtermOrProductId: string, kpi: string, allowOnlyCompareMatch = false): void {
        /**
         * Clears the loaded diagram data from a specific term an channel combination
         * @type {number}
         */
        const matchAll = (data: KpiPackage): boolean =>
            data.channel.name === channelName && data.searchtermOrProductId === searchtermOrProductId && data.kpi === kpi;
        const matchOnlyCompare = (data: KpiPackage): boolean =>
            data.channel.name === channelName && data.searchtermOrProductId === searchtermOrProductId && data.kpi === kpi && data.isCompareDiagram;

        const compareToUse = allowOnlyCompareMatch ? matchOnlyCompare : matchAll;

        let i = this.loadedDiagramData.length;
        while (i--) {
            const loadedDiagram = this.loadedDiagramData[i];
            if (compareToUse(loadedDiagram)) {
                this.loadedDiagramData.splice(i, 1);
            }
        }
    }

    /**
     * endDate
     * @param value
     */
    public changeDiagramYScale = (value: boolean) => {
        this.diagramYScaleAbsolute = value;
        this.updateDiagramView();
    };

    /**
     * After a change in the daterange picker, this function reloads the tables and the diagram data to fit the newly selected interval
     */
    public reloadDataFromBackend() {
        this.diagramData = [];
        this.loadedDiagramData = [];

        if (this.mainStore.diagramMode !== DiagramMode.None) {
            this.reloadDiagramData();
        }
    }

    private loadedAndRequestedDiagramDataAreDifferent(): boolean {
        const {allowDateCompareRange} = this.timeState;
        const {loadedDiagramData, requestedNormalDiagrams, requestedChannelDiagrams} = this;
        if (loadedDiagramData.length !== requestedChannelDiagrams.length + this.requestedNormalDiagrams.length) {
            return true;
        }

        const neededMatches = allowDateCompareRange ? 2 : 1;

        const channelDiagramsDiffer: boolean = requestedChannelDiagrams.some((requestedChannelDiagram) => {
            const matchingLoadedDiagrams = loadedDiagramData.filter(
                (loadedDiagram) =>
                    loadedDiagram.channel.name === requestedChannelDiagram.channelName &&
                    loadedDiagram.kpi === requestedChannelDiagram.kpi &&
                    loadedDiagram.isChannelDiagram &&
                    loadedDiagram.testId === requestedChannelDiagram.testId &&
                    loadedDiagram.variantId === requestedChannelDiagram.variantId
            );
            return matchingLoadedDiagrams.length !== neededMatches;
        });

        if (channelDiagramsDiffer) {
            return true;
        }

        return requestedNormalDiagrams.some((requestedNormalDiagram) => {
            const matchingLoadedDiagrams = loadedDiagramData.filter(
                (loadedDiagram) =>
                    loadedDiagram.channel.name === requestedNormalDiagram.channelName &&
                    loadedDiagram.kpi === requestedNormalDiagram.kpi &&
                    !loadedDiagram.isChannelDiagram &&
                    loadedDiagram.searchtermOrProductId === requestedNormalDiagram.searchtermOrProductId &&
                    loadedDiagram.testId === requestedNormalDiagram.testId &&
                    loadedDiagram.variantId === requestedNormalDiagram.variantId
            );
            return matchingLoadedDiagrams.length !== neededMatches;
        });
    }

    public getCurrentDataVersionInfo(checkDifferencesBetweenLoadedAndRequestedDiagrams = false): DataVersionInfo {
        const {mainDateRange, compareDateRange, allowDateCompareRange} = this.timeState;
        return {
            channel: {name: 'line diagrams are not dependant on channels', currency: 'EUR'},
            diagramMode: this.mainStore.diagramMode,
            allowDateCompareRange,
            differenceBetweenRequestedAndLoadedDiagramsDetected: checkDifferencesBetweenLoadedAndRequestedDiagrams
                ? this.loadedAndRequestedDiagramDataAreDifferent()
                : false,
            mainDateRange: DateUtils.copyDateRange(mainDateRange),
            compareDateRange: DateUtils.copyDateRange(compareDateRange),
        };
    }

    public reloadDataIfNecessary(): void {
        if (!this.dataVersionChecker.checkIfDataVersionAlreadyExists(this.getCurrentDataVersionInfo(true))) {
            this.reloadDataFromBackend();
        }
    }

    private addNewDiagramDataToOldDiagramData(newData: LineChartData[]): LineChartData[] {
        const {diagramData} = this;
        newData.forEach((newDiagramData) => {
            const foundOldEntryIndex = diagramData.findIndex((oldDiagramData) => {
                return oldDiagramData.date.isSame(newDiagramData.date);
            });
            if (foundOldEntryIndex !== -1) {
                diagramData[foundOldEntryIndex] = {
                    date: newDiagramData.date,
                    data: [...diagramData[foundOldEntryIndex].data, ...newDiagramData.data],
                };
            } else {
                diagramData.unshift(newDiagramData);
            }
        });

        return _.sortBy(diagramData, ['date']);
    }

    public addToDiagram(backendData: TimeSeriesEntry[], kpiRequestInfo: KpiPackage, dateRange: DateRange): void {
        const {kpi} = kpiRequestInfo;
        const tableColumn = this.mainStore.selectorTableStore.getTableColumnForKpi(kpi);
        const isRate = tableColumn && tableColumn.isRate === true;
        const totalRateCount = _.sum(backendData.map((data) => data.count));
        const reformattedBackendData = backendData.map((data) => {
            return {
                date: moment(data.timestamp, 'x'),
                data: [
                    {
                        ...kpiRequestInfo,
                        value: data.count,
                        isRate,
                        relValue: isRate ? data.count / totalRateCount : data.relCount,
                    },
                ],
            };
        });
        this.diagramData = this.addNewDiagramDataToOldDiagramData(reformattedBackendData);
    }

    /**
     * Frontend 24h diagram data aggregation. Basis is the normally requested hourly diagram data
     * @param lineDiagramData
     * @returns {{name: string; data}[]}
     */
    public convertTo24HourAggregationToDiagramData(lineDiagramData: LineChartData[], visibleAttribute: VisibleAttribute[]): BarChartData[] {
        const createIndex = (data: KpiPackage) =>
            data.channel + data.searchtermOrProductId + data.kpi + data.isChannelDiagram + data.isCompareDiagram + data.testId + data.variantId;

        const indexList = visibleAttribute.reduce((indexListEntries, singleVisibleAttribute) => {
            indexListEntries[createIndex(singleVisibleAttribute.kpiRequestInfo)] = singleVisibleAttribute.kpiRequestInfo;
            return indexListEntries;
        }, {} as {[index: string]: KpiPackage});

        const hourData: {[index: string]: {[hour: string]: number}} = {};
        const hourCount: {[index: string]: {[hour: string]: number}} = {};
        const sums: {[index: string]: number} = {};

        // sum up all hour values for each hour of the day [0-23]
        lineDiagramData.forEach((singleLineDiagramData) => {
            const {date, data} = singleLineDiagramData;
            data.forEach((singleData: KpiDataPackage) => {
                const index = createIndex(singleData);
                // aggregate per channelTerm per hour
                if (hourData[index] === undefined) {
                    hourData[index] = {};
                }
                if (hourData[index][date.hour()] === undefined) {
                    hourData[index][date.hour()] = 0;
                }
                hourData[index][date.hour()] += singleData.value;
                // sum up values per channelTerm for relCount later
                if (sums[index] === undefined) {
                    sums[index] = 0;
                }
                sums[index] += singleData.value;
                // data count per hour
                if (hourCount[index] === undefined) {
                    hourCount[index] = {};
                }
                if (hourCount[index][date.hour()] === undefined) {
                    hourCount[index][date.hour()] = 0;
                }
                hourCount[index][date.hour()]++;
            });
        });

        interface AggrRecord extends KpiPackage {
            value: number;
            relValue: number;
        }
        const preAggregatedData: {[index: string]: AggrRecord[]} = {};
        Object.keys(hourData).forEach((index) => {
            const kpiPackage = indexList[index];
            Object.keys(hourData[index]).forEach((hour) => {
                if (preAggregatedData[hour] === undefined) {
                    preAggregatedData[hour] = [];
                }
                const avgCountForHour = hourData[index][hour] / hourCount[index][hour];
                preAggregatedData[hour].push({
                    ...kpiPackage,
                    value: avgCountForHour,
                    relValue: sums[index] !== 0 ? hourData[index][hour] / sums[index] : 0,
                });
            });
        });

        return Object.keys(preAggregatedData).map((hour) => {
            return {
                name: hour,
                data: preAggregatedData[hour],
            };
        });
    }

    public getDiagramColor = (channelName: string, searchtermOrProductId: string, kpi: string, testId?: string, variantId?: string): ColorHelper => {
        const colorInfo = this.termColorIndexes.find(
            (termColor) =>
                termColor.searchtermOrProductId === searchtermOrProductId &&
                termColor.kpi === kpi &&
                termColor.channelName === channelName &&
                termColor.testId === testId &&
                termColor.variantId === variantId
        );
        if (colorInfo !== undefined) {
            const colorHelper = DIAGRAM_COLORS[colorInfo.colorIndex || 0];
            if (colorHelper !== undefined) {
                return colorHelper;
            }
        }
        return color(BlackColor);
    };

    public getLineAttributes(): VisibleAttribute[] {
        const {checkIfKpiRequiresMoneyFormat} = this.mainStore.selectorTableStore;
        return this.loadedDiagramData.map((loadedDiagram) => {
            return {
                color: this.getDiagramColor(
                    loadedDiagram.channel.name,
                    loadedDiagram.searchtermOrProductId,
                    loadedDiagram.kpi,
                    loadedDiagram.testId,
                    loadedDiagram.variantId
                ),
                opacity: this.getOpacityForChannelTerm(
                    loadedDiagram.channel,
                    loadedDiagram.searchtermOrProductId,
                    loadedDiagram.kpi,
                    loadedDiagram.testId,
                    loadedDiagram.variantId
                ),
                kpiRequestInfo: loadedDiagram,
                moneyFormatting: checkIfKpiRequiresMoneyFormat(loadedDiagram.kpi),
                useAbsValue: this.diagramYScaleAbsolute,
            };
        });
    }

    public getOpacityForChannelTerm(channel: Channel, searchtermOrProductId: string, kpi: string, testId?: string, variantId?: string): number {
        const {selectedGraphInDiagram} = this;
        if (selectedGraphInDiagram.length > 0) {
            return selectedGraphInDiagram.some(
                (selectedGraph) =>
                    selectedGraph.searchtermOrProductId === searchtermOrProductId &&
                    selectedGraph.channelName === channel.name &&
                    selectedGraph.kpi === kpi &&
                    selectedGraph.testId === testId &&
                    selectedGraph.variantId === variantId
            )
                ? 1
                : 0.2;
        }
        return 1;
    }

    public addToLoadedDiagramData(newLoadedDiagramData: LoadedDiagramData): void {
        const matchingEntryFound = this.loadedDiagramData.some(
            (loadedEntry) =>
                loadedEntry.searchtermOrProductId === newLoadedDiagramData.searchtermOrProductId &&
                loadedEntry.isChannelDiagram === newLoadedDiagramData.isChannelDiagram &&
                loadedEntry.kpi === newLoadedDiagramData.kpi &&
                loadedEntry.channel.name === newLoadedDiagramData.channel.name &&
                loadedEntry.isCompareDiagram === newLoadedDiagramData.isCompareDiagram &&
                loadedEntry.testId === newLoadedDiagramData.testId &&
                loadedEntry.variantId === newLoadedDiagramData.variantId
        );

        if (!matchingEntryFound) {
            this.loadedDiagramData.push(newLoadedDiagramData);
        }
    }

    public getChannelCurrenciesOfRequestedDiagramItems() {
        const channelCurrenciesChannelDiagrams = this.requestedChannelDiagrams.map(
            (channelDiagram) => this.channelState.getFullChannelForChannelName(channelDiagram.channelName).currency
        );
        const channelCurrenciesNormalDiagrams = this.requestedNormalDiagrams.map(
            (normalDiagrams) => this.channelState.getFullChannelForChannelName(normalDiagrams.channelName).currency
        );
        return _.uniq([...channelCurrenciesChannelDiagrams, ...channelCurrenciesNormalDiagrams]);
    }

    public enforceChannelInTimeSeriesEntries = (timeSeriesEntries: TimeSeriesEntry[], channelName: string): TimeSeriesEntry[] => {
        return timeSeriesEntries.map((entry) => ({...entry, channel: channelName}));
    };
}
export default GeneralLineDiagramStore;



// WEBPACK FOOTER //
// ./src/stores/GeneralLineDiagramStore.ts